import React from "react";
import { Box } from "@mui/material";
import { Formik, Form } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import Button from "../Button";
import ThemeEditor from "../Theming/ThemeEditor";
import { RESOURCE_TYPES } from "../Theming/themeConfig";
import MatchGalleryToggle from "../Theming/MatchGalleryToggle";
import LinkVaultPreview from "../WheelGallery/GallerySettings/children/LinkVault/children/LinkVaultPreview";
import {
  selectProfile,
  updateProfile as updateProfileReducer,
} from "../../redux/reducers/gallery";
import { useUserUpdateProfile } from "../../services/mutations";
import { QUERY_KEYS } from "../../utils/config";

// ThemesPopup's "LinkVault" section. VaultDesign (the existing "Vault
// Design" tab inside LinkVault's own editor) reads vault_theme off Formik
// context, so this hosts the SAME ThemeEditor(linkvault) it wraps in a
// minimal Formik whose ONLY field is vault_theme -- saving here never
// touches external_links, unlike the full LinkVault editor's combined
// submit (see GallerySettings/children/LinkVault/index.jsx). Mirrors the
// orphaned Customize hub's LinkVaultThemePanel
// (WheelGallery/GallerySettings/children/Customize/index.jsx), which solved
// this exact "theme-only save" problem first.
const LinkVaultThemeSection = () => {
  const profile = useSelector(selectProfile) || {};
  const dispatch = useDispatch();
  const updateProfile = useUserUpdateProfile();
  const queryClient = useQueryClient();

  return (
    <Formik
      enableReinitialize
      initialValues={{ vault_theme: profile.vault_theme ?? {} }}
      onSubmit={(values, helpers) => {
        updateProfile.mutate(
          { user_profile: { vault_theme: values.vault_theme } },
          {
            onSuccess: (response) => {
              dispatch(updateProfileReducer({ updates: response?.data }));
              if (profile?.user_id) {
                queryClient.invalidateQueries([QUERY_KEYS.useGetUserProfileById, profile.user_id]);
              }
              helpers.setSubmitting(false);
              toast.success("LinkVault theme saved!");
            },
            onError: () => {
              helpers.setSubmitting(false);
              toast.error("Failed to save LinkVault theme.");
            },
          }
        );
      }}
    >
      {({ values, setFieldValue, handleSubmit, isSubmitting }) => (
        <Form>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 3 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MatchGalleryToggle
                data-testid="linkvault-match-gallery"
                value={values.vault_theme?.match_gallery ? { on: true } : {}}
                tokens={["on"]}
                onChange={(mg) =>
                  setFieldValue("vault_theme", {
                    ...values.vault_theme,
                    match_gallery: !!mg.on,
                  })
                }
              />
              <ThemeEditor
                resourceType={RESOURCE_TYPES.LINKVAULT}
                value={values.vault_theme}
                onChange={(next) => setFieldValue("vault_theme", next)}
                hideMatchGallery
              />
              <Button
                data-testid="linkvault-theme-save"
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{ width: 130, marginTop: 8 }}
              >
                {isSubmitting ? "Saving…" : "Save"}
              </Button>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <LinkVaultPreview links={profile?.external_links} themeOverride={values.vault_theme} />
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default LinkVaultThemeSection;
